import CalourosImage from 'assets/img/campanhas-de-marketing/calouros.jpg';
import VeteranosImage from 'assets/img/campanhas-de-marketing/veteranos.jpg';
import ExAlunosImage from 'assets/img/campanhas-de-marketing/ex-alunos.jpg';
import FiesProuniImage from 'assets/img/campanhas-de-marketing/fies-e-prouni.jpg';
import ModalImageOne from 'assets/img/campanhas-de-marketing/modal-1.jpg';
import ModalImageTwo from 'assets/img/campanhas-de-marketing/modal-2.jpg';
import ModalImageThree from 'assets/img/campanhas-de-marketing/modal-3.jpg';
import {
  CampaignFormState,
  CampaignOnboardingCookieState,
  CampaignOnboardingStep,
  CampaignProfile,
  CampaignReducerAction,
  CampaignSelectOption,
  CampaignUploadResult,
  CreateCampaignValidationParams,
} from 'models/campaign-mkt';
import { CampanhaValues, CsvModel, CsvRegisterProp } from 'models/campanhas';

export const campaignProfiles: CampaignProfile[] = [
  {
    audience: 'Calouros',
    title: 'Calouros',
    description:
      'Apresente o crédito educacional antes que a mensalidade vire obstáculo para o ingresso, a matrícula ou o início da jornada acadêmica.',
    image: FiesProuniImage,
  },
  {
    audience: 'Veteranos',
    title: 'Veteranos',
    description:
      'Apoie estudantes em curso, trancados, evadidos ou em risco de transferência com campanhas que ajudam a preservar vínculo, matrícula e continuidade.',
    image: ExAlunosImage,
  },
  {
    audience: 'Ex-alunos',
    title: 'Ex-alunos',
    description:
      'Já conhecem a sua marca. Convide esse público para uma nova graduação, pós-graduação ou especialização com o crédito como facilitador.',
    image: VeteranosImage,
  },
  {
    audience: 'FIES & PROUNI',
    title: 'FIES e PROUNI',
    description:
      'Nem sempre bolsa ou financiamento resolvem tudo. Ofereça uma alternativa para não contemplados, com bolsa parcial ou necessidade de complemento.',
    image: CalourosImage,
  },
];

export const onboardingSteps: CampaignOnboardingStep[] = [
  {
    title: 'Campanhas que aproximam estudantes da Fundacred',
    text:
      'Use esta seção para enviar listas de estudantes da sua instituição e acionar campanhas de e-mail marketing da Fundacred, de acordo com o momento da jornada de cada público.',
    image: ModalImageOne,
  },
  {
    title: 'Escolha o público certo para cada objetivo',
    text:
      'Você pode criar campanhas para apoiar calouros, reengajar veteranos, convidar ex-alunos para uma nova formação ou oferecer uma alternativa a estudantes vinculados ao FIES e ProUni.',
    image: ModalImageTwo,
  },
  {
    title: 'A Fundacred ajuda na comunicação',
    text:
      'Depois de enviar a lista, a Fundacred realiza os disparos da campanha para apresentar o crédito educacional como uma possibilidade de ingresso, permanência ou retomada dos estudos.',
    image: ModalImageThree,
  },
];
export const preloadOnboardingImages = (): void => {
  if (typeof window === 'undefined') return;

  onboardingSteps.forEach(({ image }) => {
    const preloadImage = new Image();
    preloadImage.src = image;
  });
};

const CAMPAIGN_ONBOARDING_COOKIE_PREFIX = 'campaign-onboarding';
const CAMPAIGN_ONBOARDING_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;
const CAMPAIGN_ONBOARDING_MAX_DAYS = 5;
const MILLISECONDS_IN_DAY = 24 * 60 * 60 * 1000;

const normalizeCampaignOnboardingEmail = (email: string): string =>
  email.trim().toLowerCase();

const normalizeCampaignUploadFileNamePart = (value: string): string =>
  value
    .trim()
    .replace(/[<>:"/\\|?*]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');

const getStorageItemByKeySuffix = (
  storage: Storage,
  suffix: string,
): string => {
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);

    if (key?.endsWith(suffix)) {
      return storage.getItem(key) || '';
    }
  }

  return '';
};

const getCampaignInstitutionName = (): string => {
  if (typeof window === 'undefined') return '';

  const storedEmail =
    localStorage.getItem('NPSemailIES') ||
    sessionStorage.getItem('email') ||
    localStorage.getItem('email') ||
    getStorageItemByKeySuffix(sessionStorage, '.email') ||
    getStorageItemByKeySuffix(localStorage, '.email') ||
    '';
  try {
    const user = JSON.parse(localStorage.getItem('usuarioIES') || '{}');
    const institutionName = user?.email || storedEmail;

    return typeof institutionName === 'string' ? institutionName : storedEmail;
  } catch {
    return storedEmail;
  }
};
const getFormattedCampaignInstitutionName = (): string =>
  normalizeCampaignUploadFileNamePart(
    getCampaignInstitutionName(),
  ).toLowerCase();

const getNormalizedCampaignName = (campaignName: string): string =>
  campaignName.trim().replace(/\s+/g, ' ') || 'campanha';

const campaignNameHasInstitution = (
  campaignName: string,
  institutionName: string,
): boolean => {
  const normalizedCampaignName = normalizeCampaignUploadFileNamePart(
    campaignName,
  ).toLowerCase();
  const normalizedInstitutionName = normalizeCampaignUploadFileNamePart(
    institutionName,
  ).toLowerCase();

  return normalizedCampaignName.endsWith(`-${normalizedInstitutionName}`);
};

export const getCampaignNameWithInstitution = (
  campaignName: string,
): string => {
  const normalizedCampaignName = getNormalizedCampaignName(campaignName);
  const institutionName = getFormattedCampaignInstitutionName();

  if (campaignNameHasInstitution(normalizedCampaignName, institutionName)) {
    return normalizedCampaignName;
  }

  return `${normalizedCampaignName} - ${institutionName}`;
};

export const getCampaignDisplayName = (campaignName: string): string => {
  const institutionName = getFormattedCampaignInstitutionName();
  const institutionSuffix = ` - ${institutionName}`;

  if (
    institutionName &&
    campaignName.toLowerCase().endsWith(institutionSuffix.toLowerCase())
  ) {
    return campaignName.slice(0, -institutionSuffix.length);
  }

  return campaignName;
};
const formatCampaignFileDate = (date: Date): string => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  return `${day}-${month}-${year}`;
};

export const getCampaignFileNameBase = (
  campaignName: string,
  date = new Date(),
): string => {
  const normalizedCampaignName =
    normalizeCampaignUploadFileNamePart(campaignName) || 'campanha';
  const institutionName = getFormattedCampaignInstitutionName();
  const campaignFileName = campaignNameHasInstitution(
    normalizedCampaignName,
    institutionName,
  )
    ? normalizedCampaignName
    : `${normalizedCampaignName}-${institutionName}`;

  return `${campaignFileName}-${formatCampaignFileDate(date)}`;
};

export const getCampaignUploadFileName = (
  campaignName: string,
  originalFileName: string,
): string => {
  const extension = originalFileName.split('.').pop()?.toLowerCase() || 'csv';

  return `${getCampaignFileNameBase(campaignName)}.${extension}`;
};

export const getCreateCampaignPayload = (
  formValues: CampaignFormState,
): CampaignFormState => {
  const payload = { ...formValues };
  payload[CampanhaValues.NOME_CAMPANHA] = getCampaignNameWithInstitution(
    String(payload[CampanhaValues.NOME_CAMPANHA] || 'campanha'),
  );
  delete payload[CampanhaValues.DURACAO];

  return payload;
};

export const isCreateCampaignFormInvalid = ({
  audience,
  file,
  fileError,
  formValues,
}: CreateCampaignValidationParams): boolean =>
  !audience ||
  !formValues[CampanhaValues.NOME_CAMPANHA] ||
  !formValues[CampanhaValues.GRAU_DE_ENSINO] ||
  !formValues[CampanhaValues.TIPO_DE_CAMPANHA] ||
  !formValues[CampanhaValues.DATA_DE_INICIO] ||
  !formValues[CampanhaValues.DATA_DE_FIM] ||
  !formValues[CampanhaValues.DURACAO] ||
  !file ||
  fileError;

export const parseCampaignErrorCsv = (data: string): CsvModel[] => {
  const [, ...content] = data.replace(/\r\n/g, '\n').split('\n');

  return content.filter(Boolean).map((item) => {
    const [name, email, phone, logs] = item.split(';');

    return {
      Nome: name,
      'E-mail': email,
      Telefone: phone,
      Erro: logs,
    };
  });
};

export const getCampaignModalInformation = ({
  numLinhasErro,
  numLinhasSucesso,
  numeroLinhas,
}: CampaignUploadResult): CsvRegisterProp[] => [
  { text: 'Linhas enviadas', total: numLinhasSucesso },
  { text: 'Linhas com erro', total: numLinhasErro },
  { text: 'TOTAL', total: numeroLinhas },
];

const getCampaignOnboardingCookieName = (email: string): string =>
  `${CAMPAIGN_ONBOARDING_COOKIE_PREFIX}-${encodeURIComponent(email)}`;

const getTodayDateString = (): string => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const isValidDateString = (date: string): boolean =>
  /^\d{4}-\d{2}-\d{2}$/.test(date);

const getDateTime = (date: string): number => {
  const [year, month, day] = date.split('-').map(Number);

  return new Date(year, month - 1, day).getTime();
};

const getDaysBetween = (startDate: string, endDate: string): number =>
  Math.floor(
    (getDateTime(startDate) - getDateTime(endDate)) / MILLISECONDS_IN_DAY,
  );

const getCookieValue = (cookieName: string): string => {
  if (typeof document === 'undefined') return '';

  const cookiePrefix = `${cookieName}=`;
  const cookie = document.cookie
    .split('; ')
    .find((item) => item.indexOf(cookiePrefix) === 0);

  return cookie ? cookie.substring(cookiePrefix.length) : '';
};

const getCampaignOnboardingState = (
  email: string,
): CampaignOnboardingCookieState | null => {
  const cookieValue = getCookieValue(getCampaignOnboardingCookieName(email));

  if (!cookieValue) return null;

  try {
    const parsedState = JSON.parse(
      decodeURIComponent(cookieValue),
    ) as CampaignOnboardingCookieState;

    if (
      !isValidDateString(parsedState.firstShownDate) ||
      !Array.isArray(parsedState.shownDates)
    ) {
      return null;
    }

    return {
      firstShownDate: parsedState.firstShownDate,
      shownDates: parsedState.shownDates.filter((date) =>
        isValidDateString(date),
      ),
    };
  } catch {
    return null;
  }
};

const setCampaignOnboardingState = (
  email: string,
  state: CampaignOnboardingCookieState,
): void => {
  if (typeof document === 'undefined') return;

  const cookieName = getCampaignOnboardingCookieName(email);
  const cookieValue = encodeURIComponent(JSON.stringify(state));
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';

  document.cookie = `${cookieName}=${cookieValue}; Max-Age=${CAMPAIGN_ONBOARDING_COOKIE_MAX_AGE_SECONDS}; Path=/; SameSite=Lax${secure}`;
};

export const getCampaignOnboardingUserEmail = (): string => {
  if (typeof window === 'undefined') return '';

  const npsEmail = localStorage.getItem('NPSemailIES');

  if (npsEmail) return normalizeCampaignOnboardingEmail(npsEmail);

  try {
    const user = JSON.parse(localStorage.getItem('usuarioIES') || '{}');

    return typeof user?.email === 'string'
      ? normalizeCampaignOnboardingEmail(user.email)
      : '';
  } catch {
    return '';
  }
};

export const shouldShowCampaignOnboarding = (email: string): boolean => {
  const normalizedEmail = normalizeCampaignOnboardingEmail(email);

  if (!normalizedEmail) return false;

  const today = getTodayDateString();
  const state = getCampaignOnboardingState(normalizedEmail);

  if (!state) return true;
  if (state.shownDates.indexOf(today) > -1) return false;
  if (state.shownDates.length >= CAMPAIGN_ONBOARDING_MAX_DAYS) return false;

  return (
    getDaysBetween(today, state.firstShownDate) < CAMPAIGN_ONBOARDING_MAX_DAYS
  );
};

export const setCampaignOnboardingClose = (email: string): void => {
  const normalizedEmail = normalizeCampaignOnboardingEmail(email);

  if (!normalizedEmail) return;

  const today = getTodayDateString();
  const state = getCampaignOnboardingState(normalizedEmail);
  const shownDates = state?.shownDates || [];
  const nextShownDates =
    shownDates.indexOf(today) > -1 ? shownDates : [...shownDates, today];

  setCampaignOnboardingState(normalizedEmail, {
    firstShownDate: state?.firstShownDate || today,
    shownDates: nextShownDates.slice(0, CAMPAIGN_ONBOARDING_MAX_DAYS),
  });
};

export const tiposCampanha = [
  { id: 'Calouros', label: 'Calouros' },
  { id: 'Calouros evadidos com débito', label: 'Calouros evadidos com débito' },
  { id: 'Calouros evadidos sem débito', label: 'Calouros evadidos sem débito' },
  {
    id: 'Ex-alunos segunda graduação ou pós-graduação',
    label: 'Ex-alunos segunda graduação ou pós-graduação',
  },
  { id: 'FIES', label: 'FIES' },
  { id: 'PROUNI', label: 'PROUNI' },
  {
    id: 'Veteranos com trancamento de matrícula com débito',
    label: 'Veteranos com trancamento de matrícula com débito',
  },
  {
    id: 'Veteranos com trancamento de matrícula sem débito',
    label: 'Veteranos com trancamento de matrícula sem débito',
  },
  {
    id: 'Veteranos em transferência para outra IES',
    label: 'Veteranos em transferência para outra IES',
  },
  {
    id: 'Veteranos evadidos sem débito',
    label: 'Veteranos evadidos sem débito',
  },
  {
    id: 'Veteranos evadidos com débito',
    label: 'Veteranos evadidos com débito',
  },
  {
    id: 'Veteranos matriculados (em curso) sem débito',
    label: 'Veteranos matriculados (em curso) sem débito',
  },
  {
    id: 'Veteranos matriculados (em curso) com débito',
    label: 'Veteranos matriculados (em curso) com débito',
  },
];

export const publicosCampanha: CampaignSelectOption[] = [
  { id: 'Calouros', label: 'Calouros' },
  { id: 'Veteranos', label: 'Veteranos' },
  { id: 'Ex-alunos', label: 'Ex-alunos' },
  { id: 'FIES & PROUNI', label: 'FIES & PROUNI' },
];

export const contextosCampanha: Record<string, CampaignSelectOption[]> = {
  Calouros: [
    { id: 'Calouros', label: 'Calouros (Padrão)' },
    {
      id: 'Calouros evadidos com débito',
      label: 'Calouros evadidos com débito',
    },
    {
      id: 'Calouros evadidos sem débito',
      label: 'Calouros evadidos sem débito',
    },
  ],
  Veteranos: [
    {
      id: 'Veteranos com trancamento de matrícula com débito',
      label: 'Veteranos com trancamento de matrícula com débito',
    },
    {
      id: 'Veteranos com trancamento de matrícula sem débito',
      label: 'Veteranos com trancamento de matrícula sem débito',
    },
    {
      id: 'Veteranos em transferência para outra IES',
      label: 'Veteranos em transferência para outra instituição',
    },
    {
      id: 'Veteranos evadidos com débito',
      label: 'Veteranos evadidos com débito',
    },
    {
      id: 'Veteranos evadidos sem débito',
      label: 'Veteranos evadidos sem débito',
    },
    {
      id: 'Veteranos matriculados (em curso) sem débito',
      label: 'Veteranos matriculados/em curso sem débito',
    },
    {
      id: 'Veteranos matriculados (em curso) com débito',
      label: 'Veteranos matriculados/em curso com débito',
    },
  ],
  'Ex-alunos': [
    {
      id: 'Ex-alunos segunda graduação ou pós-graduação',
      label: 'Ex-alunos para segunda graduação ou pós-graduação',
    },
  ],
  'FIES & PROUNI': [
    { id: 'FIES', label: 'FIES' },
    { id: 'PROUNI', label: 'PROUNI' },
  ],
};

export const graus = [
  { id: 'Cursos Livres', label: 'Cursos Livres' },
  { id: 'Ensino Fundamental', label: 'Ensino Fundamental' },
  { id: 'Ensino Médio', label: 'Ensino Médio' },
  { id: 'Ensino Técnico', label: 'Ensino Técnico' },
  { id: 'Graduação', label: 'Graduação' },
  { id: 'Pós-graduação', label: 'Pós-graduação' },
];

export const statusCampanha = [
  { id: 'em-andamento', label: 'Em andamento' },
  { id: 'finalizado', label: 'Finalizado' },
];

export const duracaoCampanha = [
  { id: '15', label: '15 dias' },
  { id: '30', label: '30 dias' },
  { id: '45', label: '45 dias' },
];

export const initialState = {
  [CampanhaValues.NOME_CAMPANHA]: '',
  [CampanhaValues.TIPO_DE_CAMPANHA]: '',
  [CampanhaValues.GRAU_DE_ENSINO]: '',
  [CampanhaValues.DATA_DE_INICIO]: '',
  [CampanhaValues.DATA_DE_FIM]: '',
  [CampanhaValues.DURACAO]: '',
};

export const campanhaReducer = (
  state: CampaignFormState,
  action: CampaignReducerAction,
): CampaignFormState => {
  switch (action.type) {
    case 'INPUT_CHANGE':
      if (!action.input) return state;

      return {
        ...state,
        [action.input.name]: action.input.value,
      };

    case 'RESET_VALUES':
      return initialState;

    default:
      return state;
  }
};

export const initialErrorState = {
  [CampanhaValues.NOME_CAMPANHA]: false,
  [CampanhaValues.DATA_DE_INICIO]: false,
  [CampanhaValues.TIPO_DE_CAMPANHA]: false,
  [CampanhaValues.GRAU_DE_ENSINO]: false,
  [CampanhaValues.DURACAO]: false,
};

export const columnTable = [
  'Nome da Campanha',
  'Tipo de Campanha',
  'Grau de Ensino',
  'Contatos',
  'Status',
  'Ações',
];
